// External
import { useRef } from 'react';

// Internal
import { logger } from '@/lib/logger';
import type { LogOnShippingChangeReceivedToFPTIPayload } from './types';
import { isFunction } from '@/lib/utils/typeUtils';
import { features } from '@/lib/features';
import { getToken } from '@paypalcorp/c3-shared-globals';
import {
  getCallbackIntegrationType,
  getCallbackVersion,
  getCheckoutSessionFlags,
} from '../../globals';

const logOnShippingAddressChangeReceivedToFPTI = (
  payload: LogOnShippingChangeReceivedToFPTIPayload,
  isServerSideShippingCb?: boolean,
): void => {
  const { errorDescription = 'none', status } = payload;
  const baseEventName = 'checkout_merchant_shipping_calculation_received';
  const eventName = isServerSideShippingCb
    ? `${baseEventName}_sssc`
    : baseEventName;

  logger.fpti(eventName, {
    api_response: status,
    error_description: errorDescription,
    state_name: 'review',
    transition_name: 'process_review_merchant_shipping_calculation',
  });

  logger.info('shipping_address_change_received', {
    api_response: status,
    error_description: errorDescription,
    event_name: eventName,
  });
};

const logShippingOptionsChangeReceivedToFPTI = (
  payload: LogOnShippingChangeReceivedToFPTIPayload,
  isServerSideShippingCb?: boolean,
): void => {
  const baseEventName = 'checkout_merchant_shipping_option_change_received';
  const eventName = isServerSideShippingCb
    ? `${baseEventName}_sssc`
    : baseEventName;

  const { errorDescription = 'none', status } = payload;
  logger.fpti(eventName, {
    api_response: status,
    error_description: errorDescription,
    state_name: 'review',
    transition_name: 'process_review_merchant_shipping_option_calculation',
  });

  logger.info('shipping_options_change_received', {
    api_response: status,
    error_description: errorDescription,
    event_name: eventName,
  });
};

const getShippingCallbackType = (): 'server_side' | 'client' | 'none' => {
  const isServerSideShippingCallbacksEligible = features.isActive(
    'isServerSideShippingCallbacksEligible',
  );
  const isClientCallbacks =
    features.isActive('isShippingCallbacksV2Eligible') ||
    features.isActive('isShippingCallbackV1Eligible');

  // Server callbacks take precedence over client callbacks
  if (isServerSideShippingCallbacksEligible) {
    return 'server_side';
  }

  return isClientCallbacks ? 'client' : 'none';
};

const getShippingCallbackVersion = (): 'V1' | 'V2' | 'none' => {
  const shippingCallbackVersion = getCallbackVersion();
  if (shippingCallbackVersion === 'V2') {
    return 'V2';
  } else if (shippingCallbackVersion === 'V1') {
    return 'V1';
  }
  return 'none';
};

const logServerSideShippingCallbacksEligibility = (): void => {
  const isShippingChangeAllowed = Boolean(
    getCheckoutSessionFlags().isChangeShippingAddressAllowed,
  );
  const hideShipping = Boolean(getCheckoutSessionFlags().hideShipping);
  const integrationType = getCallbackIntegrationType();
  const callbackVersion = getCallbackVersion();

  const isEligible =
    integrationType === 'SERVER_SIDE' &&
    callbackVersion === 'V2' &&
    isShippingChangeAllowed &&
    !hideShipping;

  const eventName = `sssc_${
    isEligible ? 'eligible' : 'ineligible'
  }_with_integration_type_${integrationType}_version_${callbackVersion}`;

  logger.info(eventName, {
    callbackVersion,
    hideShipping,
    integrationType,
    isEligible,
    isShippingChangeAllowed,
  });
};

const logShippingCallbacksInfoToFPTI = (): void => {
  logServerSideShippingCallbacksEligibility();
  const shippingCallbackType = getShippingCallbackType();
  const shippingCallbackVersion = getShippingCallbackVersion();

  logger.fpti('sdk_shipping_callback', {
    event_name: 'sdk_integration_call_received',
    shipping_address_change_callback: isFunction(
      window.xprops?.onShippingAddressChange,
    ),
    shipping_change_callback: isFunction(window.xprops?.onShippingChange),
    shipping_options_change_callback: isFunction(
      window.xprops?.onShippingOptionsChange,
    ),
  });

  logger.fpti('shipping_callbacks_type', {
    shipping_callbacks_type: shippingCallbackType,
    shipping_callbacks_version: shippingCallbackVersion,
  });
  logger.info(
    `shipping_callbacks_type_${shippingCallbackType}_version_${shippingCallbackVersion}`,
  );
};

/**
 * Logs the shipping callback Customer Perceived Latency (CPL) to their respective events.
 * @param {string} eventName
 * @param {Number} cpl - Customer Perceived Latency () in milliseconds.
 */
const logOnShippingCallbackLatencyEvt = (
  eventName: string,
  cpl: number,
): void => {
  logger.fpti(eventName, {
    component: 'checkoutuinodeweb',
    context_id: getToken(),
    cpl_query_metrics: cpl,
    event_name: eventName,
    evnt_type: 'im', // (Impression) User saw something
  });

  logger.info('shipping_callback_latency', {
    cpl_milliseconds: cpl,
    event_name: eventName,
  });
};

export type LatencyLog = {
  isRunning: boolean;
  from: number | null;
  to: number | null;
};

const latencyLogger =
  (
    callbackOperation: 'address' | 'options',
    callbackType: 'server_side' | 'client',
  ) =>
  (log: LatencyLog): void => {
    let eventName;
    if (callbackOperation === 'address') {
      eventName =
        callbackType === 'server_side'
          ? 'guest_xo_sssc_address_call_cpl'
          : 'guest_xo_address_call_cpl';
    } else {
      eventName =
        callbackType === 'server_side'
          ? 'guest_xo_sssc_options_call_cpl'
          : 'guest_xo_options_call_cpl';
    }

    if (log.to && log.from) {
      const customerPerceivedLatency = log.to - log.from;
      logOnShippingCallbackLatencyEvt(eventName, customerPerceivedLatency);
    }
  };
type UseLatencyLoggerReturn = {
  startLogging: () => void;
  finishLogging: () => void;
};
/**
 * Used to log the latency of certain events in the application.
 */
const useLatencyLogger = (
  onLog: (log: LatencyLog) => void,
): UseLatencyLoggerReturn => {
  const { current: latencyLog } = useRef<LatencyLog>({
    from: null,
    isRunning: false,
    to: null,
  });

  function resetLog() {
    latencyLog.isRunning = false;
    latencyLog.from = null;
    latencyLog.to = null;
  }

  function startLogging() {
    resetLog();

    latencyLog.isRunning = true;
    latencyLog.from = Date.now();
  }

  function finishLogging() {
    if (!latencyLog.isRunning) {
      return;
    }

    latencyLog.to = Date.now();
    latencyLog.isRunning = false;

    onLog(latencyLog);

    resetLog();
  }

  return {
    finishLogging,
    startLogging,
  };
};

export {
  latencyLogger,
  logOnShippingAddressChangeReceivedToFPTI,
  logShippingOptionsChangeReceivedToFPTI,
  logShippingCallbacksInfoToFPTI,
  useLatencyLogger,
};
